import React from 'react';
import cx from 'classnames';
import './PageSection.scss';

/**
 * PageSection Component
 * 
 * A semantic section wrapper that provides background styling and layout structure.
 * Designed to work with the ESS Design System container components.
 * 
 * Features:
 * - Semantic HTML5 section element
 * - Customizable background colors
 * - Multiple variants for different section types
 * - Full-width background support
 * - Integration with ESS design tokens
 */

type PageSectionVariant = 'default' | 'hero' | 'content' | 'footer' | 'minimal';

interface PageSectionProps {
  /** Content to be rendered inside the section */
  children: React.ReactNode;
  
  /** Background color for the section (CSS color value or design token) */
  backgroundColor?: string;
  
  /** Visual variant of the section */
  variant?: PageSectionVariant;
  
  /** Additional CSS classes */
  className?: string;
  
  /** HTML element to render as (default: section) */
  as?: React.ElementType;
  
  /** Whether section should span full viewport width */
  fullWidth?: boolean;
  
  /** Whether to include default padding */
  padding?: boolean;
  
  /** Unique identifier for the section */
  id?: string;
  
  /** Accessible label for the section */
  ariaLabel?: string;
  
  /** ID of element that labels this section */
  ariaLabelledBy?: string;
  
  /** Top padding */
  paddingTop?: string;

  /** Bottom padding */
  paddingBottom?: string;

  /** Top border */
  borderTop?: string;

  /** Bottom border */
  borderBottom?: string;

  /** Additional HTML attributes */
  style?: React.CSSProperties;
  [key: string]: any;
}

const PageSection = React.forwardRef<HTMLElement, PageSectionProps>(({
  children,
  backgroundColor,
  variant = 'default',
  className,
  as: Component = 'section',
  fullWidth = true,
  padding = true,
  id,
  ariaLabel,
  ariaLabelledBy,
  paddingTop = 'var(--page-section-paddingtb)',
  paddingBottom  = 'var(--page-section-paddingtb)',
  borderTop,
  borderBottom,
  ...props
}, ref) => {
  const sectionClasses = cx(
    'ess-page-section',
    `ess-page-section--${variant}`,
    {
      'ess-page-section--full-width': fullWidth,
      'ess-page-section--no-padding': !padding,
    },
    className,
  );

  const sectionStyle: React.CSSProperties = {
    ...(backgroundColor && { backgroundColor }),
    paddingTop,
    paddingBottom,
    borderTop,
    borderBottom,
    ...props.style,
  };

  return (
    <Component
      ref={ref}
      className={sectionClasses}
      style={sectionStyle}
      id={id}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      {...props}
    >
      {children}
    </Component>
  );
});

export default PageSection;
